import type { ApiResult } from '@/services/apiClient';
import { apiClient } from '@/services/apiClient';
import type {
  M365ActivityDigest,
  M365CalendarEvent,
  M365ConnectionStatus,
  M365DriveItem,
  M365DriveSyncResult,
  M365MailFolder,
  M365MailMessage,
  M365Notebook,
  M365OAuthUrl,
  M365Page,
  M365Section,
  M365SharePointItem,
  M365SharePointLibrary,
  M365SharePointSite,
  M365TodoList,
  M365TodoTask,
} from './m365Workspace.types';

const BASE = '/m365-workspace';

export const m365WorkspaceApi = {
  mailMessagesPage(
    params: { folderId?: string; top?: number; search?: string; cursor?: string } = {},
  ): Promise<ApiResult<{ items: M365MailMessage[]; nextCursor: string | null }>> {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params))
      if (value !== undefined) search.set(key, String(value));
    return apiClient.get(`${BASE}/mail/messages/page?${search}`);
  },

  crmSeedApply(
    messageIds: string[],
  ): Promise<
    ApiResult<{
      accepted: number;
      skipped: number;
      outcomes: Array<{ messageId: string; applied: boolean; detail: string }>;
    }>
  > {
    return apiClient.post(`${BASE}/crm-seed/apply`, { messageIds });
  },
  status(): Promise<ApiResult<M365ConnectionStatus>> {
    return apiClient.get(`${BASE}/status`);
  },

  oauthUrl(): Promise<ApiResult<M365OAuthUrl>> {
    return apiClient.get(`${BASE}/oauth-url`);
  },

  oauthCallback(input: {
    code: string;
    state: string;
  }): Promise<ApiResult<{ connected: boolean; connection: M365ConnectionStatus['connection'] }>> {
    return apiClient.post(`${BASE}/oauth-callback`, input);
  },

  disconnect(): Promise<ApiResult<{ disconnected: true }>> {
    return apiClient.delete(`${BASE}/connection`);
  },

  crmSeedPreview(input: {
    candidates: Array<{
      graphMessageId: string;
      subject: string | null;
      fromAddress: string | null;
      receivedAt: string | null;
      preview: string | null;
    }>;
  }): Promise<
    ApiResult<{
      accepted: number;
      skipped: number;
      detail: string;
      candidates: typeof input.candidates;
    }>
  > {
    return apiClient.post(`${BASE}/crm-seed/preview`, input);
  },

  mailFolders(): Promise<ApiResult<M365MailFolder[]>> {
    return apiClient.get(`${BASE}/mail/folders`);
  },

  mailMessages(
    params: {
      folderId?: string;
      top?: number;
      search?: string;
    } = {},
  ): Promise<ApiResult<M365MailMessage[]>> {
    const search = new URLSearchParams();
    if (params.folderId) search.set('folderId', params.folderId);
    if (params.top) search.set('top', String(params.top));
    if (params.search) search.set('search', params.search);
    const query = search.toString();
    return apiClient.get(`${BASE}/mail/messages${query ? `?${query}` : ''}`);
  },

  mailMessage(messageId: string): Promise<ApiResult<M365MailMessage>> {
    return apiClient.get(`${BASE}/mail/messages/${encodeURIComponent(messageId)}`);
  },

  sendMail(input: {
    to: string[];
    cc?: string[];
    subject: string;
    bodyHtml: string;
  }): Promise<ApiResult<{ sent: true }>> {
    return apiClient.post(`${BASE}/mail/send`, input);
  },

  replyMail(
    messageId: string,
    input: { bodyHtml: string; replyAll?: boolean },
  ): Promise<ApiResult<{ sent: true }>> {
    return apiClient.post(`${BASE}/mail/messages/${encodeURIComponent(messageId)}/reply`, input);
  },

  markRead(messageId: string, isRead: boolean): Promise<ApiResult<{ updated: true }>> {
    return apiClient.patch(`${BASE}/mail/messages/${encodeURIComponent(messageId)}/read`, {
      isRead,
    });
  },

  moveMail(messageId: string, destinationFolderId: string): Promise<ApiResult<{ moved: true }>> {
    return apiClient.post(`${BASE}/mail/messages/${encodeURIComponent(messageId)}/move`, {
      destinationFolderId,
    });
  },

  calendarEvents(start: string, end: string): Promise<ApiResult<M365CalendarEvent[]>> {
    const search = new URLSearchParams({ start, end });
    return apiClient.get(`${BASE}/calendar/events?${search.toString()}`);
  },

  createEvent(input: {
    subject: string;
    start: string;
    end: string;
    location?: string;
    bodyHtml?: string;
  }): Promise<ApiResult<M365CalendarEvent>> {
    return apiClient.post(`${BASE}/calendar/events`, input);
  },

  updateEvent(
    eventId: string,
    input: {
      subject?: string;
      start?: string;
      end?: string;
      location?: string;
    },
  ): Promise<ApiResult<M365CalendarEvent>> {
    return apiClient.patch(`${BASE}/calendar/events/${encodeURIComponent(eventId)}`, input);
  },

  deleteEvent(eventId: string): Promise<ApiResult<{ deleted: true }>> {
    return apiClient.delete(`${BASE}/calendar/events/${encodeURIComponent(eventId)}`);
  },

  todoLists(): Promise<ApiResult<M365TodoList[]>> {
    return apiClient.get(`${BASE}/tasks/lists`);
  },

  todoTasks(listId: string): Promise<ApiResult<M365TodoTask[]>> {
    return apiClient.get(`${BASE}/tasks/lists/${encodeURIComponent(listId)}/tasks`);
  },

  createTask(
    listId: string,
    input: { title: string; dueDateTime?: string; importance?: 'low' | 'normal' | 'high' },
  ): Promise<ApiResult<M365TodoTask>> {
    return apiClient.post(`${BASE}/tasks/lists/${encodeURIComponent(listId)}/tasks`, input);
  },

  updateTask(
    listId: string,
    taskId: string,
    input: { status?: string; title?: string; importance?: string; dueDateTime?: string | null },
  ): Promise<ApiResult<M365TodoTask>> {
    return apiClient.patch(
      `${BASE}/tasks/lists/${encodeURIComponent(listId)}/tasks/${encodeURIComponent(taskId)}`,
      input,
    );
  },

  notebooks(): Promise<ApiResult<M365Notebook[]>> {
    return apiClient.get(`${BASE}/notes/notebooks`);
  },

  sections(notebookId: string): Promise<ApiResult<M365Section[]>> {
    return apiClient.get(`${BASE}/notes/notebooks/${encodeURIComponent(notebookId)}/sections`);
  },

  pages(sectionId: string): Promise<ApiResult<M365Page[]>> {
    return apiClient.get(`${BASE}/notes/sections/${encodeURIComponent(sectionId)}/pages`);
  },

  pageContent(pageId: string): Promise<ApiResult<{ html: string }>> {
    return apiClient.get(`${BASE}/notes/pages/${encodeURIComponent(pageId)}/content`);
  },

  createPage(
    sectionId: string,
    input: { title: string; html: string },
  ): Promise<ApiResult<M365Page>> {
    return apiClient.post(`${BASE}/notes/sections/${encodeURIComponent(sectionId)}/pages`, input);
  },

  listFiles(itemId?: string): Promise<ApiResult<M365DriveItem[]>> {
    const search = new URLSearchParams();
    if (itemId) search.set('itemId', itemId);
    const query = search.toString();
    return apiClient.get(`${BASE}/files${query ? `?${query}` : ''}`);
  },

  downloadUrl(itemId: string): Promise<ApiResult<{ url: string; name: string | null }>> {
    return apiClient.get(`${BASE}/files/${encodeURIComponent(itemId)}/download-url`);
  },

  renameFile(itemId: string, name: string): Promise<ApiResult<M365DriveItem>> {
    return apiClient.patch(`${BASE}/files/${encodeURIComponent(itemId)}`, { name });
  },

  deleteFile(itemId: string): Promise<ApiResult<{ deleted: true }>> {
    return apiClient.delete(`${BASE}/files/${encodeURIComponent(itemId)}`);
  },

  syncToLibrary(input: {
    itemIds?: string[];
    fullDelta?: boolean;
  }): Promise<ApiResult<M365DriveSyncResult>> {
    return apiClient.post(`${BASE}/files/sync-to-library`, input);
  },

  sharePointSites(query?: string): Promise<ApiResult<M365SharePointSite[]>> {
    const search = new URLSearchParams();
    if (query?.trim()) search.set('q', query.trim());
    const qs = search.toString();
    return apiClient.get(`${BASE}/sharepoint/sites${qs ? `?${qs}` : ''}`);
  },

  sharePointLibraries(siteId: string): Promise<ApiResult<M365SharePointLibrary[]>> {
    return apiClient.get(`${BASE}/sharepoint/sites/${encodeURIComponent(siteId)}/libraries`);
  },

  sharePointItems(driveId: string, itemId?: string): Promise<ApiResult<M365SharePointItem[]>> {
    const search = new URLSearchParams();
    if (itemId) search.set('itemId', itemId);
    const qs = search.toString();
    return apiClient.get(
      `${BASE}/sharepoint/drives/${encodeURIComponent(driveId)}/items${qs ? `?${qs}` : ''}`,
    );
  },

  sharePointDownloadUrl(
    driveId: string,
    itemId: string,
  ): Promise<ApiResult<{ url: string; name: string | null; webUrl: string | null }>> {
    return apiClient.get(
      `${BASE}/sharepoint/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/download-url`,
    );
  },

  activityDigest(): Promise<ApiResult<M365ActivityDigest>> {
    return apiClient.get(`${BASE}/activity-digest`);
  },
};
